<section class="container-fluid bg-white find-provider-page">
  <div class="container py-5">
    <div class="row">
      <div class="col-md-10 offset-md-1 text-black">
        <div class="finder-hero text-center mb-4">
          <h1 class="mb-3">Find a Family Health Advantage Provider</h1>
          <h4 class="mb-0">
            Don't see your clinic?
            <a class="text-black d-inline-block fw-bold" routerLink="/contact">Click Here</a>
            and we will contact them!
          </h4>
        </div>

        <div class="finder-search-card shadow-sm rounded-3 mb-4">
          <h2 class="h4 fw-bold mb-2">Find a provider location</h2>
          <p class="text-muted mb-3">Enter a zip code, city, state, clinic name, or provider name.</p>

          <div class="finder-search-row">
            <div class="finder-search-input">
              <i class="bi bi-search"></i>
              <input
                type="text"
                class="form-control rounded-3"
                placeholder="Enter zip code or city, state"
                [value]="locationSearch.value()"
                (input)="onSearchInput(searchInput.value)"
                (keyup.enter)="findProviders()"
                #searchInput />
            </div>

            <div class="finder-radius-select">
              <label class="form-label small text-muted fw-bold mb-1" for="providerRadius">Radius</label>
              <select
                id="providerRadius"
                class="form-select rounded-3"
                [value]="selectedRadius()"
                (change)="onRadiusChange(radiusSelect.value)"
                #radiusSelect>
                @for(radius of radiusOptions; track radius) {
                  <option [value]="radius">
                    {{ radius === 'all' ? 'All' : radius + ' miles' }}
                  </option>
                }
              </select>
            </div>

            <button
              type="button"
              class="btn btn-black rounded-pill px-4 finder-search-button"
              (click)="findProviders()">
              Find Provider
            </button>
          </div>

          <div class="mt-3 d-flex flex-column flex-md-row justify-content-between gap-2">
            <button
              type="button"
              class="btn btn-link text-black p-0 use-location-link align-self-start"
              [disabled]="distanceLoading()"
              (click)="useCurrentLocation()">
              <i class="bi bi-geo-alt me-1"></i>
              Use my current location
            </button>

            @if(locationSearch.value() || usingCurrentLocation()) {
              <button
                class="btn btn-outline-secondary btn-sm rounded-0 align-self-start align-self-md-center"
                type="button"
                (click)="reset()">
                Clear Filters
              </button>
            }
          </div>
        </div>

        @if(locationError()) {
          <div class="alert alert-warning rounded-0">
            <i class="bi bi-exclamation-triangle me-2"></i>
            {{ locationError() }}
          </div>
        }

        @if(loading()) {
          <div class="alert alert-info py-2 my-3 rounded-0">
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Loading provider locations...
          </div>
        }

        @if(distanceLoading()) {
          <div class="alert alert-info py-2 my-3 rounded-0">
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Finding nearest provider locations...
          </div>
        }

        <div class="mt-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 class="mb-1">Results</h5>
              @if(usingCurrentLocation()) {
                <div class="text-muted small">
                  @if(selectedRadius() === 'all') {
                    Showing all provider locations near your current location.
                  } @else {
                    Showing provider locations within {{ getSelectedRadiusLabel() }} of your current location.
                  }
                </div>
              } @else {
                <div class="text-muted small">
                  Showing all provider locations. Search or use your current location to narrow results.
                </div>
              }
            </div>
          </div>

          @if(filterClinicData().length) {
            <p class="text-muted mb-3">
              {{ filterClinicData().length }} provider location{{ filterClinicData().length === 1 ? '' : 's' }} found
            </p>

            @for(p of visibleClinicData(); track p.location_id) {
              <div class="col-12 mb-3">
                <div class="card shadow-sm border-0 rounded-3 provider-card">
                  <div class="card-body p-4">
                    <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                      <div class="d-flex align-items-start">
                        <div class="provider-icon me-3 d-flex align-items-center justify-content-center rounded-circle bg-dark text-white">
                          <i class="bi bi-hospital fs-4"></i>
                        </div>

                        <div>
                          <h5 class="card-title mb-1 fw-bold">
                            {{ p.clinic_name || p.name || 'Provider Location' }}
                          </h5>

                          <div class="text-muted small">
                            <i class="bi bi-building me-1"></i>
                            Participating Provider Location
                          </div>
                        </div>
                      </div>

                      @if (p.distance_miles !== null && p.distance_miles !== undefined) {
                        <span class="badge rounded-pill bg-light text-dark border fs-6">
                          <i class="bi bi-geo-alt me-1"></i>
                          {{ p.distance_miles | number:'1.1-1' }} miles away
                        </span>
                      }
                    </div>

                    <div class="row g-3">
                      <div class="col-12">
                        <div class="small text-muted fw-bold mb-1">
                          <i class="bi bi-geo-alt me-1"></i>
                          Address
                        </div>
                        <div>
                          {{ p.address || 'Unavailable' }}
                          @if(p.address2) {
                            <div>{{ p.address2 }}</div>
                          }
                          <div>
                            {{ p.city || 'Unavailable' }}, {{ p.state || 'Unavailable' }} {{ p.zip_code || 'Unavailable' }}
                          </div>
                          <div class="mt-2">
                            <span class="small text-muted fw-bold">
                              <i class="bi bi-telephone me-1"></i>
                              Phone
                            </span>
                            <div>{{ formatPhoneNumber(p.phone) }}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    @if(p.clinic_website) {
                      <div class="mt-3">
                        <a class="btn btn-sm btn-outline-dark rounded-0" [href]="p.clinic_website" target="_blank" rel="noopener noreferrer">
                          <i class="bi bi-globe me-1"></i>
                          Visit Clinic Website
                        </a>
                      </div>
                    }

                    @if(p.providers.length) {
                      <div class="border-top pt-3 mt-3">
                        <div class="small text-muted fw-bold mb-2">
                          <i class="bi bi-person-badge me-1"></i>
                          Providers at this location
                        </div>

                        <div class="d-flex flex-wrap gap-2">
                          @for(provider of p.providers; track provider.id) {
                            <span class="badge rounded-pill bg-light text-dark border px-3 py-2">
                              <i class="bi bi-person me-1"></i>
                              {{ provider.first_name }} {{ provider.last_name }}
                            </span>
                          }
                        </div>
                      </div>
                    }
                  </div>
                </div>
              </div>
            }

            @if(hasMoreResults()) {
              <div class="text-center mt-4">
                <button
                  type="button"
                  class="btn btn-outline-dark rounded-pill px-4"
                  (click)="showMoreResults()">
                  Show more providers
                </button>
              </div>
            }
          } @else if(!loading() && !distanceLoading()) {
            <div class="alert alert-light border" role="alert">
              @if(showBeyondRadiusLink()) {
                <div class="fw-semibold mb-2">
                  No providers found within {{ getSelectedRadiusLabel() }}.
                </div>
                <button
                  type="button"
                  class="btn btn-link text-black fw-bold p-0 nearest-beyond-link"
                  (click)="showNearestBeyondRadius()">
                  Nearest providers beyond 50 miles
                </button>
              } @else {
                No providers match your filters. Try broadening your search.
              }
            </div>
          }
        </div>
      </div>
    </div>
  </div>
</section>
